import { useEffect } from 'react';

import { useQuery } from '@zenbusiness/application-commons-graphql';
import Grid from '@zenbusiness/zui/Grid';
import Heading from '@zenbusiness/zui/Heading';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';

import FormStepModal from '#client/components/FormStepModal';
import { FocusGrid, ContainerGrid, ActionButtonsGrid } from '#client/components/Grid';
import { ACTION_BUTTON_TYPE } from '#client/components/Grid/constants';
import { FAQ_OVERVIEW_URL } from '#client/constants/FAQs';
import useBreakpoints from '#client/hooks/useBreakpoints';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import { useLogError } from '#client/hooks/useLogError';
import useModalContext from '#client/hooks/useModalContext';
import useModalDisplayTracker from '#client/hooks/useModalDisplayTracker';
import useToastContext from '#client/hooks/useToastContext';
import { GET_APPLICATION_QUERY } from '#client/queries';
import { formatStringWithOnlyDot } from '#client/utils/text';

import {
  ContentWrapper,
  DialogContentStyled,
  BoxContentStyled,
  LinkStyled,
  WrapperFAQStyled
} from './styles';

export default function LinkBankAccountErrorModal({ handleClose, metadata, owner }) {
  const {
    sizes: { isDesktop }
  } = useBreakpoints();
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { createCommonErrorToast } = useToastContext();
  const { clearAllModal } = useModalContext();
  const {
    loading: applicationLoading,
    error: applicationError,
    errors,
    data: applicationResponseData
  } = useQuery(GET_APPLICATION_QUERY, {
    variables: {
      input: {
        businessUuid: businessEntityUuid
      }
    },
    noThrow: true
  });

  useLogError(errors, applicationError);
  const businessLegalName = applicationResponseData?.application?.name ?? '';
  const bank = metadata?.institution?.name ?? '';
  const accountNumber = formatStringWithOnlyDot(metadata?.account?.mask ?? '');

  const handleCloseModal = () => {
    clearAllModal();
    handleClose();
  };

  useEffect(() => {
    if (applicationError) {
      createCommonErrorToast({ error: applicationError });
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationError]);

  useModalDisplayTracker({
    elementName: 'name_mismatch_plaid_linking',
    elementText: 'name_mismatch_plaid_linking',
    appPageName: 'active_banking_dashboard'
  });

  return (
    <FormStepModal
      handleClose={handleCloseModal}
      isHideBackButton
      title="Link a bank account"
    >
      <DialogContentStyled role="form">
        <ContainerGrid>
          <FocusGrid>
            <ContentWrapper spacing={{ xs: '32px', md: '40px' }}>
              <Stack
                alignItems="center"
                direction={isDesktop ? 'row' : 'column'}
                justifyContent="center"
                spacing="8px"
              >
                <Stack justifyContent="center">
                  <SharedAsset
                    alt=""
                    src="/icon/micro/x-circle.svg"
                    width={isDesktop ? 50 : 40}
                  />
                </Stack>
                <Heading
                  align="center"
                  size="3xl"
                  weight="bold"
                >
                  Your account could not be linked
                </Heading>
              </Stack>
              <BoxContentStyled spacing={{ xs: '8px', md: '16px' }}>
                <Stack spacing="8px">
                  <Text
                    size="xl"
                    weight="medium"
                  >
                    Names are mismatched
                  </Text>
                  <Stack spacing="16px">
                    <Text
                      color="tertiary"
                      size="sm"
                    >
                      The account name on the account you&apos;re trying to link must match the
                      account holder&apos;s name or legal business name associated with your
                      ZenBusiness account.
                    </Text>
                    <Text
                      color="tertiary"
                      size="sm"
                    >
                      To send money to this account, you can add it as a payee.
                    </Text>
                  </Stack>
                </Stack>
                <Grid
                  columns={{ xs: 12, md: 6 }}
                  container
                  rowSpacing={{ xs: '16px', md: '32px' }}
                >
                  <Grid
                    item
                    md={3}
                    xs={12}
                  >
                    <Stack spacing="8px">
                      <Text
                        size="xs"
                        uppercase
                      >
                        Account holder
                      </Text>
                      <Text size="lg">{owner}</Text>
                    </Stack>
                  </Grid>
                  <Grid
                    item
                    md={3}
                    xs={12}
                  >
                    <Stack spacing="8px">
                      <Text
                        size="xs"
                        uppercase
                      >
                        Business legal name
                      </Text>
                      <Text size="lg">{businessLegalName}</Text>
                    </Stack>
                  </Grid>
                  <Grid
                    item
                    md={3}
                    xs={12}
                  >
                    <Stack spacing="8px">
                      <Text
                        size="xs"
                        uppercase
                      >
                        Bank
                      </Text>
                      <Text size="lg">{bank}</Text>
                    </Stack>
                  </Grid>
                  <Grid
                    item
                    md={3}
                    xs={12}
                  >
                    <Stack spacing="8px">
                      <Text
                        size="xs"
                        uppercase
                      >
                        Account
                      </Text>
                      <Text size="lg">{accountNumber}</Text>
                    </Stack>
                  </Grid>
                </Grid>
              </BoxContentStyled>
              <WrapperFAQStyled>
                <Text
                  color="tertiary"
                  size="sm"
                >
                  Need more help?
                  <LinkStyled
                    color="info"
                    onClick={() => window.open(FAQ_OVERVIEW_URL)}
                  >
                    {' '}
                    FAQs
                  </LinkStyled>
                </Text>
              </WrapperFAQStyled>
            </ContentWrapper>
          </FocusGrid>
        </ContainerGrid>
      </DialogContentStyled>
      <ActionButtonsGrid
        sx={{
          zIndex: 9
        }}
        type={ACTION_BUTTON_TYPE.SECONDARY}
      >
        <LoadingButton
          color="primary"
          disabled={applicationLoading}
          fullWidth
          onClick={handleClose}
          size="large"
          variant="contained"
        >
          Try again
        </LoadingButton>
      </ActionButtonsGrid>
    </FormStepModal>
  );
}

LinkBankAccountErrorModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  metadata: PropTypes.shape({
    account: PropTypes.shape({
      mask: PropTypes.string
    }),
    institution: PropTypes.shape({
      name: PropTypes.string
    })
  }),
  owner: PropTypes.string
};
